import { Skeleton, Tabs } from '@mantine/core';
import { useResetAtom } from 'jotai/utils';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { screenshotDiffAtom } from '~/atoms/atoms';
import { useQueryParam } from '~/hooks/nav/useQueryParam';
import { api } from '~/trpc-extra/api';
import RunTable from '../tables/run/RunTable';
import TestRunDiffViewTab from './TestRunDiffViewTab';
import TestRunReplayTab from './TestRunReplayTab';
import TestRunExecutionViewTab from './ExecutionView/TestRunExecutionViewTab';

const HistorySkeleton = () => (
  <div className="space-y-4">
    <Skeleton
      height={40}
      radius="md"
    />
    <Skeleton
      height={30}
      radius="md"
    />
    <Skeleton
      height="40vh"
      radius="md"
    />
  </div>
);

export default function TestRunContent({
  runId,
  isFullView,
  className = '',
  initialTab,
}: {
  runId: string;
  isFullView?: boolean;
  className?: string;
  initialTab?: string;
}) {
  const router = useRouter();
  const resetScreenshotDiff = useResetAtom(screenshotDiffAtom);

  const { data: testRun, isLoading: isTestRunLoading } = api.testRun.get.useQuery({ runId });

  // For drawer mode - we want to maintain local state
  const [localTab, setLocalTab] = useState<string>(initialTab || (router.query.tab as string) || 'diffView');
  const [tab, setTab] = useQueryParam('tab');

  // Use URL parameters in full view, local state in drawer view
  const activeTab = isFullView ? tab || 'replay' : localTab;
  const setActiveTab = (value: string | null) =>
    isFullView ? setTab(value || 'replay') : setLocalTab(value || 'replay');

  useEffect(() => {
    return () => {
      resetScreenshotDiff();
    };
  }, [resetScreenshotDiff]);

  return (
    <div className={className}>
      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        styles={{
          list: {
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: 'var(--mantine-color-body)',
          },
        }}
        classNames={{
          root: 'h-full flex flex-col',
          panel: 'flex flex-col flex-1',
        }}
        keepMounted={false}
      >
        <Tabs.List>
          <Tabs.Tab value="replay">Playwright Trace</Tabs.Tab>
          {!!testRun && <Tabs.Tab value="stablyTrace">Stably Trace</Tabs.Tab>}
          {!!testRun && <Tabs.Tab value="diffView">Auto-heal Changes</Tabs.Tab>}
          <Tabs.Tab value="history">Test History</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel
          value="replay"
          className="mt-4"
        >
          <TestRunReplayTab
            runId={runId}
            isFullView={isFullView}
          />
        </Tabs.Panel>

        <Tabs.Panel value="diffView">
          {!!testRun && (
            <TestRunDiffViewTab
              isTestRunLoading={isTestRunLoading}
              testRun={testRun}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="stablyTrace">
          {!!testRun && (
            <TestRunExecutionViewTab
              isTestRunLoading={isTestRunLoading}
              testRun={testRun}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="history">
          <div className="h-full">
            {isTestRunLoading ?
              <HistorySkeleton />
            : testRun ?
              <RunTable
                testId={testRun.test.id}
                runId={runId}
              />
            : <div className="mt-4">No test history available.</div>}
          </div>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}
